const chapter = {
  id: 'reproductive-health',
  slug: 'reproductive-health',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Reproductive Health',
        hindi: 'जनन स्वास्थ्य',
  },
  estReadMinutes: 45,
  difficulty: {
    english: 'Easy',
        hindi: 'आसान',
  },
  weightage: {
    english: '2-3 questions in NEET',
        hindi: 'NEET में 2-3 प्रश्न',
  },
  syllabusTags: ['Reproduction', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'definition-strategies',
      title: {
        english: 'Reproductive Health & National Strategies',
        hindi: 'जनन स्वास्थ्य और राष्ट्रीय नीतियाँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'WHO definition: total well-being in physical, emotional, behavioural & social aspects related to reproduction.',
        hindi: 'WHO की परिभाषा: प्रजनन से संबंधित शारीरिक, भावनात्मक, व्यवहारिक और सामाजिक पहलुओं में संपूर्ण कल्याण।',
            },
            {
              english: 'India was first country to launch nation-wide programme — **Family Planning (1951)**, now broadened as **RCH (Reproductive & Child Health Care)**.',
        hindi: 'भारत राष्ट्रव्यापी कार्यक्रम शुरू करने वाला पहला देश था - **परिवार नियोजन (1951)**, जिसे अब **आरसीएच (प्रजनन एवं बाल स्वास्थ्य देखभाल)** के रूप में विस्तारित किया गया है।',
            },
            {
              english: 'Goals: create awareness, provide facilities & support for reproductive health, control population, prevent STIs.',
        hindi: 'लक्ष्य: जागरूकता पैदा करना, प्रजनन स्वास्थ्य के लिए सुविधाएं और सहायता प्रदान करना, जनसंख्या को नियंत्रित करना, एसटीआई को रोकना।',
            },
            {
              english: 'Programs include — sex education in schools, awareness about adolescent issues (menstruation, safe sex, pregnancy, STIs, abortion, contraception), care for pregnant mothers, post-natal care of mother & child, infertility treatment.',
        hindi: 'कार्यक्रमों में शामिल हैं - स्कूलों में यौन शिक्षा, किशोर मुद्दों (मासिक धर्म, सुरक्षित यौन संबंध, गर्भावस्था, एसटीआई, गर्भपात, गर्भनिरोधक), गर्भवती माताओं की देखभाल, प्रसवोत्तर मां और बच्चे की देखभाल, बांझपन उपचार के बारे में जागरूकता।',
            },
            {
              english: 'Indicators of improved reproductive health — better identification & care for STIs, ↓ MMR & IMR, ↑ medically assisted deliveries, ↓ population growth.',
        hindi: 'बेहतर प्रजनन स्वास्थ्य के संकेतक - एसटीआई के लिए बेहतर पहचान और देखभाल, ↓ एमएमआर और आईएमआर, ↑ चिकित्सकीय सहायता से प्रसव, ↓ जनसंख्या वृद्धि।',
            },
          ],
        },
      ],
    },
    {
      id: 'population',
      title: {
        english: 'Population Explosion & Birth Control',
        hindi: 'जनसंख्या विस्फोट और जन्म नियन्त्रण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'World population: 2 billion (1900) → 6 billion (2000) → > 7 billion now. India: 350 million (1947) → > 1 billion (2000).',
        hindi: 'विश्व जनसंख्या: 2 अरब (1900) → 6 अरब (2000) → > 7 अरब अब। भारत: 350 मिलियन (1947) → > 1 बिलियन (2000)।',
            },
            {
              english: 'Reasons for explosion — ↓ MMR, ↓ IMR, ↑ no. of girls reaching reproductive age.',
        hindi: 'विस्फोट के कारण - ↓ एमएमआर, ↓ आईएमआर, ↑ नहीं। प्रजनन आयु तक पहुँचने वाली लड़कियों की संख्या। **भारत में वैधानिक प्रजनन आयु** - 18 वर्ष (लड़की), 21 वर्ष (लड़का)।',
            },
            {
              english: '**Statutory Reproductive Age in India** — 18 yrs (girl), 21 yrs (boy).',
        hindi: '**भारत में वैधानिक प्रजनन आयु** - 18 वर्ष (लड़की), 21 वर्ष (लड़का)।',
            },
            {
              english: 'Govt. has set replacement-level fertility (RLF) — couples having only 2 children → ~stable population.',
        hindi: 'सरकार। प्रतिस्थापन-स्तर प्रजनन क्षमता (आरएलएफ) निर्धारित की गई है - केवल 2 बच्चे वाले जोड़े → ~ स्थिर जनसंख्या।',
            },
            {
              english: 'Slogans: "Hum do, hamare do".',
        hindi: 'नारे: "हम दो, हमारे दो"।',
            },
          ],
        },
      ],
    },
    {
      id: 'contraception',
      title: {
        english: 'Contraceptive Methods',
        hindi: 'गर्भनिरोधक विधियाँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Ideal contraceptive — user-friendly, easily available, effective, **reversible**, no/minimum side-effects, no interference with sexual drive.',
        hindi: 'आदर्श गर्भनिरोधक - उपयोगकर्ता के अनुकूल, आसानी से उपलब्ध, प्रभावी, **प्रतिवर्ती**, no/minimum दुष्प्रभाव, यौन इच्छा में कोई हस्तक्षेप नहीं।',
            },
            {
              english: '**1. Natural / Traditional**:',
        hindi: '**1. प्राकृतिक/पारंपरिक**:',
            },
            {
              english: '• **Periodic abstinence** — avoid coitus from day 10 to 17 of cycle (likely ovulation).',
        hindi: '• **आवधिक संयम** - चक्र के 10 से 17वें दिन (संभावित ओव्यूलेशन) तक सहवास से बचें।',
            },
            {
              english: '• **Coitus interruptus / withdrawal** — withdrawal of penis before ejaculation.',
        hindi: '• **सहवास में रुकावट / वापसी** - स्खलन से पहले लिंग को बाहर निकालना।',
            },
            {
              english: '• **Lactational amenorrhoea** — no ovulation while fully breast-feeding (effective up to ~6 months postpartum).',
        hindi: '• **लैक्टेशनल एमेनोरिया** - पूर्ण स्तनपान के दौरान कोई ओव्यूलेशन नहीं (प्रसव के बाद ~6 महीने तक प्रभावी)।',
            },
            {
              english: 'No side-effects but **chances of failure are high**.',
        hindi: 'कोई दुष्प्रभाव नहीं लेकिन **विफलता की संभावना अधिक है**।',
            },
            {
              english: '**2. Barrier**:',
        hindi: '**2. बाधा**:',
            },
            {
              english: '• **Condoms (Nirodh)** — male & female (latex sheath); also protect against STIs/HIV.',
        hindi: '• **कंडोम (निरोध)** - पुरुष और महिला (लेटेक्स शीथ); STIs/HIV से भी बचाव करें।',
            },
            {
              english: '• **Diaphragms, cervical caps, vaults** — reusable barriers placed in female reproductive tract; cover cervix.',
        hindi: '• **डायाफ्राम, ग्रीवा कैप, वॉल्ट** - महिला प्रजनन पथ में रखे गए पुन: प्रयोज्य अवरोध; गर्भाशय ग्रीवा को ढकें.',
            },
            {
              english: '• Often used with spermicidal creams/foams (improves effectiveness).',
        hindi: '• अक्सर शुक्राणुनाशक creams/foams (प्रभावकारिता में सुधार) के साथ प्रयोग किया जाता है।',
            },
            {
              english: '**3. IUDs (Intrauterine Devices)** — inserted by trained medics into uterus through vagina.',
        hindi: '**3. आईयूडी (अंतर्गर्भाशयी उपकरण)** - प्रशिक्षित चिकित्सकों द्वारा योनि के माध्यम से गर्भाशय में डाला जाता है।',
            },
            {
              english: '• **Non-medicated** — Lippes loop.',
        hindi: '• **गैर-औषधीय** - लिप्स लूप।',
            },
            {
              english: '• **Copper-releasing** — CuT, Cu7, Multiload 375. Cu²⁺ ions ↓ sperm motility & fertilising capacity.',
        hindi: '• **तांबा-विमोचन** - CuT, Cu7, मल्टीलोड 375। Cu²⁺ आयन ↓ शुक्राणु गतिशीलता और निषेचन क्षमता।',
            },
            {
              english: '• **Hormone-releasing** — Progestasert, LNG-20. Make uterus unsuitable for implantation; cervix hostile to sperms.',
        hindi: '• **हार्मोन-रिलीजिंग** - प्रोजेस्टासर्ट, एलएनजी-20। गर्भाशय को आरोपण के लिए अनुपयुक्त बनाना; गर्भाशय ग्रीवा शुक्राणुओं के प्रति शत्रुतापूर्ण है।',
            },
            {
              english: 'IUDs = **most widely accepted method in India**; ideal for women wanting to delay/space children.',
        hindi: 'आईयूडी = **भारत में सबसे व्यापक रूप से स्वीकृत विधि**; बच्चों को विलंबित/अंतरिक्ष में रखने की इच्छुक महिलाओं के लिए आदर्श।',
            },
            {
              english: '**4. Oral contraceptives (Pills)** — progestogens or progestogen-oestrogen combinations.',
        hindi: '**4. मौखिक गर्भनिरोधक (गोलियाँ)** - प्रोजेस्टोजेन या प्रोजेस्टोजेन-एस्ट्रोजन संयोजन।',
            },
            {
              english: '• **Saheli** (developed at CDRI Lucknow) — non-steroidal "once-a-week" pill with very few side-effects + high contraceptive value.',
        hindi: '• **सहेली** (सीडीआरआई लखनऊ में विकसित) - बहुत कम साइड-इफेक्ट + उच्च गर्भनिरोधक मूल्य वाली गैर-स्टेरायडल "सप्ताह में एक बार" गोली।',
            },
            {
              english: '• Pills inhibit ovulation & implantation; alter cervical mucus.',
        hindi: '• गोलियाँ ओव्यूलेशन और इम्प्लांटेशन को रोकती हैं; ग्रीवा बलगम को बदलें.',
            },
            {
              english: '**5. Injectables / Implants** — same principle as pills; longer duration.',
        hindi: '**5. इंजेक्शन/प्रत्यारोपण** - गोलियों के समान सिद्धांत; लंबी अवधि.',
            },
            {
              english: '**6. Emergency contraception** — progestogens / IUDs within **72 hours** of coitus → may avoid pregnancy after unprotected intercourse / rape.',
        hindi: '**6. आपातकालीन गर्भनिरोधन** - सहवास के **72 घंटों** के भीतर प्रोजेस्टोजेन / आईयूडी → असुरक्षित संभोग/बलात्कार के बाद गर्भधारण से बचा जा सकता है।',
            },
            {
              english: '**7. Surgical (Sterilisation)** — terminal method; > 99 % effective; reversal difficult.',
        hindi: '**7. सर्जिकल (नसबंदी)** - टर्मिनल विधि; >99% प्रभावी; उलटाव कठिन.',
            },
            {
              english: '• **Vasectomy** (male) — small part of vas deferens cut/tied through scrotum.',
        hindi: '• **नसबंदी** (पुरुष) - अंडकोश के माध्यम से शुक्रवाहिका का छोटा भाग cut/tied।',
            },
            {
              english: '• **Tubectomy** (female) — small part of fallopian tube cut/tied through abdomen.',
        hindi: '• **ट्यूबेक्टोमी** (महिला) - पेट के माध्यम से फैलोपियन ट्यूब cut/tied का छोटा हिस्सा।',
            },
          ],
        },
      ],
    },
    {
      id: 'mtp',
      title: {
        english: 'Medical Termination of Pregnancy (MTP)',
        hindi: 'चिकित्सीय गर्भ समापन (MTP)',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Voluntary or induced abortion. ~45-50 million MTPs globally / yr (~ 1/5 of total pregnancies).',
        hindi: 'स्वैच्छिक या प्रेरित गर्भपात। विश्व स्तर पर ~45-50 मिलियन एमटीपी/वर्ष (कुल गर्भधारण का ~1/5)।',
            },
            {
              english: 'India legalised MTP in **1971** (with strict conditions to check misuse — e.g. female foeticide).',
        hindi: 'भारत ने **1971** में एमटीपी को वैध कर दिया (दुरुपयोग को रोकने के लिए सख्त शर्तों के साथ - जैसे कन्या भ्रूण हत्या)।',
            },
            {
              english: 'Safe up to **12 weeks (1st trimester)**; risky in 2nd trimester.',
        hindi: '**12 सप्ताह (पहली तिमाही)** तक सुरक्षित; दूसरी तिमाही में जोखिम भरा।',
            },
            {
              english: 'Reasons — unwanted pregnancies (rape, contraceptive failure), foetal abnormalities, danger to mother\'s life.',
        hindi: 'कारण - अवांछित गर्भधारण (बलात्कार, गर्भनिरोधक विफलता), भ्रूण की असामान्यताएं, मां के जीवन को खतरा।',
            },
            {
              english: 'MTP Amendment Act 2021 raised the upper limit to **24 weeks** for special categories (rape victims, minors, mental illness, foetal anomalies).',
        hindi: 'एमटीपी संशोधन अधिनियम 2021 ने विशेष श्रेणियों (बलात्कार पीड़ितों, नाबालिगों, मानसिक बीमारी, भ्रूण संबंधी विसंगतियों) के लिए ऊपरी सीमा **24 सप्ताह** तक बढ़ा दी। **हेपेटाइटिस-बी, एड्स** - इंजेक्शन की सुइयां, सर्जिकल उपकरण, दूषित रक्त चढ़ाने, मां से भ्रूण को साझा करने से भी फैलता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'stis',
      title: {
        english: 'Sexually Transmitted Infections (STIs)',
        hindi: 'यौन संचारित संक्रमण (STIs)',
      },
      blocks: [
        {
          type: 'table',
          headers: ['STI / VD', 'Causative agent', 'Type'],
          rows: [
            ['Gonorrhoea', 'Neisseria gonorrhoeae', 'Bacterial'],
            ['Syphilis', 'Treponema pallidum', 'Bacterial'],
            ['Chlamydiasis', 'Chlamydia trachomatis', 'Bacterial'],
            ['Genital herpes', 'Herpes simplex virus', 'Viral'],
            ['Genital warts', 'HPV', 'Viral'],
            ['Trichomoniasis', 'Trichomonas vaginalis', 'Protozoan'],
            ['Hepatitis-B', 'HBV', 'Viral (also blood-borne)'],
            ['AIDS', 'HIV', 'Viral (also blood-borne)'],
          ],
        },
        {
          type: 'list',
          items: [
            {
              english: '**Hepatitis-B, AIDS** — also transmitted by sharing injection needles, surgical instruments, transfusion of contaminated blood, mother to foetus.',
        hindi: '**हेपेटाइटिस-बी, एड्स** - इंजेक्शन की सुइयां, सर्जिकल उपकरण साझा करने, दूषित रक्त चढ़ाने, मां से भ्रूण को भी फैलता है।',
            },
            {
              english: 'Early symptoms — itching, fluid discharge, slight pain, swelling in genital region.',
        hindi: 'प्रारंभिक लक्षण - खुजली, तरल पदार्थ का स्राव, हल्का दर्द, जननांग क्षेत्र में सूजन।',
            },
            {
              english: 'Most infections curable if detected early; some (HIV, Hepatitis-B) have no/limited cure.',
        hindi: 'यदि शीघ्र पता चल जाए तो अधिकांश संक्रमणों का इलाज संभव है; कुछ (एचआईवी, हेपेटाइटिस-बी) का इलाज no/limited है।',
            },
            {
              english: 'Highest incidence — **15-24 yrs age group**.',
        hindi: 'उच्चतम घटना - **15-24 वर्ष आयु समूह**।',
            },
            {
              english: 'Prevention slogan — "Avoid sex with unknown / multiple partners; always use condom; in case of doubt, go for early detection & complete treatment".',
        hindi: 'रोकथाम का नारा - "अज्ञात/एक से अधिक साथियों के साथ यौन संबंध बनाने से बचें; हमेशा कंडोम का उपयोग करें; संदेह की स्थिति में, शीघ्र पता लगाने और पूर्ण उपचार के लिए जाएं"।',
            },
          ],
        },
      ],
    },
    {
      id: 'infertility',
      title: {
        english: 'Infertility & ART',
        hindi: 'बन्ध्यता और सहायक जनन प्रौद्योगिकी (ART)',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Infertility = inability to conceive after 1+ year of unprotected coitus. ~15 % couples affected; often blamed on women in India (incorrect).',
        hindi: 'Infertility = inability to conceive after 1+ year of unprotected coitus। ~15% जोड़े प्रभावित; भारत में अक्सर महिलाओं पर आरोप लगाया जाता है (गलत)। **सहायक प्रजनन तकनीक (ART)**:',
            },
            {
              english: '**Assisted Reproductive Technologies (ART)**:',
        hindi: '**सहायक प्रजनन प्रौद्योगिकी (एआरटी)**:',
            },
            {
              english: '• **IVF–ET (In Vitro Fertilisation – Embryo Transfer / "test tube baby")** — ovum + sperm fused in lab dish → zygote/early embryo (≤ 8-blasto stage) transferred into fallopian tube (**ZIFT**); embryos > 8-cell transferred into uterus (**IUT**).',
        hindi: '• **आईवीएफ-ईटी (इन विट्रो फर्टिलाइजेशन - भ्रूण स्थानांतरण / "टेस्ट ट्यूब बेबी")** - डिंब + शुक्राणु लैब डिश में जुड़े हुए → zygote/early भ्रूण (≤ 8-ब्लास्टो चरण) को फैलोपियन ट्यूब में स्थानांतरित किया गया (**ZIFT**); भ्रूण > 8-कोशिका गर्भाशय में स्थानांतरित (**IUT**)।',
            },
            {
              english: '• **GIFT (Gamete Intra-Fallopian Transfer)** — transfer of ovum from donor into fallopian tube of female who can\'t produce ovum but can support fertilisation & development.',
        hindi: '• **गिफ्ट (गैमेट इंट्रा-फैलोपियन ट्रांसफर)** - दाता से अंडाणु को उस महिला के फैलोपियन ट्यूब में स्थानांतरित करना जो अंडाणु का उत्पादन नहीं कर सकती है लेकिन निषेचन और विकास का समर्थन कर सकती है।',
            },
            {
              english: '• **ICSI (Intra-Cytoplasmic Sperm Injection)** — sperm directly injected into ovum (used when sperm count is very low).',
        hindi: '• **आईसीएसआई (इंट्रा-साइटोप्लाज्मिक स्पर्म इंजेक्शन)** - शुक्राणु को सीधे डिंब में इंजेक्ट किया जाता है (शुक्राणु संख्या बहुत कम होने पर इसका उपयोग किया जाता है)।',
            },
            {
              english: '• **AI (Artificial Insemination)** — semen collected from husband / healthy donor introduced into vagina or uterus (**IUI**) of female. Used when male has low sperm count or is impotent.',
        hindi: '• **एआई (कृत्रिम गर्भाधान)** - पति/स्वस्थ दाता से एकत्रित वीर्य को महिला की योनि या गर्भाशय (**आईयूआई**) में डाला जाता है। इसका उपयोग तब किया जाता है जब पुरुष में शुक्राणुओं की संख्या कम हो या वह नपुंसक हो।',
            },
            {
              english: 'Adoption is also encouraged — many parentless / orphaned children need a family.',
        hindi: 'गोद लेने को भी प्रोत्साहित किया जाता है - कई माता-पिता रहित/अनाथ बच्चों को एक परिवार की आवश्यकता होती है।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Reproductive age (India): female 18 yr, male 21 yr}',
      desc: {
        english: '',
        hindi: 'प्रजनन आयु (भारत): महिला 18 वर्ष, पुरुष 21 वर्ष',
      },
    },
    {
      latex: '\\text{IUDs} = \\text{most accepted reversible contraceptive in India}',
      desc: {
        english: '',
        hindi: 'आईयूडी = भारत में सर्वाधिक स्वीकृत प्रतिवर्ती गर्भनिरोधक',
      },
    },
    {
      latex: '\\text{Saheli = once-a-week non-steroidal pill (CDRI, Lucknow)}',
      desc: {
        english: '',
        hindi: 'सहेली = सप्ताह में एक बार गैर-स्टेरायडल गोली (सीडीआरआई, लखनऊ)',
      },
    },
    {
      latex: '\\text{Emergency contraception within 72 h}',
      desc: {
        english: '',
        hindi: '72 घंटों के भीतर आपातकालीन गर्भनिरोधक',
      },
    },
    {
      latex: '\\text{MTP safe} \\leq 12 \\text{ weeks; up to 24 wk (special, 2021 Act)}',
      desc: {
        english: '',
        hindi: 'एमटीपी सुरक्षित 12 सप्ताह; 24 सप्ताह तक (विशेष, 2021 अधिनियम)',
      },
    },
    {
      latex: '\\text{ZIFT: } \\leq 8\\text{-cell into oviduct; IUT: } > 8\\text{-cell into uterus}',
      desc: {
        english: '',
        hindi: 'ZIFT: डिंबवाहिनी में 8-कोशिका; IUT: > गर्भाशय में 8-कोशिका',
      },
    },
    {
      latex: '\\text{ICSI: sperm injected directly into ovum (low count cases)}',
      desc: {
        english: '',
        hindi: 'आईसीएसआई: शुक्राणु को सीधे डिंब में इंजेक्ट किया जाता है (कम गिनती के मामले)',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Contraception',
      problem: {
        english: 'Saheli is a:',
        hindi: 'सहेली एक है:',
      },
      answer: {
        english: 'Non-steroidal once-a-week oral contraceptive',
        hindi: 'सप्ताह में एक बार गैर-स्टेरायडल मौखिक गर्भनिरोधक',
      },
      solution: {
        english: 'Developed by CDRI, Lucknow.',
        hindi: 'सीडीआरआई, लखनऊ द्वारा विकसित।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'STI',
      problem: {
        english: 'Causative agent of syphilis is:',
        hindi: 'सिफलिस का प्रेरक एजेंट है:',
      },
      answer: {
        english: 'Treponema pallidum',
        hindi: 'ट्रेपोनेमा पैलिडम',
      },
      solution: {
        english: 'Bacterial STI.',
        hindi: 'बैक्टीरियल एसटीआई।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'IUD',
      problem: {
        english: 'Cu-T acts as a contraceptive by:',
        hindi: 'Cu-T एक गर्भनिरोधक के रूप में कार्य करता है:',
      },
      answer: {
        english: 'Reducing sperm motility & fertilising capacity (Cu²⁺ ions)',
        hindi: 'शुक्राणु की गतिशीलता और निषेचन क्षमता को कम करना (Cu²⁺ आयन)',
      },
      solution: {
        english: 'Hormone-releasing IUDs make uterus unsuitable for implantation.',
        hindi: 'हार्मोन-रिलीजिंग आईयूडी गर्भाशय को प्रत्यारोपण के लिए अनुपयुक्त बना देते हैं।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'ART',
      problem: {
        english: 'ZIFT involves transfer of:',
        hindi: 'ZIFT में निम्न का स्थानांतरण शामिल है:',
      },
      answer: {
        english: 'Zygote / early embryo (≤ 8 cells) into fallopian tube',
        hindi: 'युग्मनज/प्रारंभिक भ्रूण (≤ 8 कोशिकाएं) फैलोपियन ट्यूब में',
      },
      solution: {
        english: 'IUT transfers > 8-cell embryo into uterus.',
        hindi: 'IUT > 8-कोशिका भ्रूण को गर्भाशय में स्थानांतरित करता है।',
      },
    },
    {
      year: 2019,
      exam: 'NEET',
      tag: 'MTP',
      problem: {
        english: 'MTP is generally considered safe up to:',
        hindi: 'एमटीपी को आम तौर पर तब तक सुरक्षित माना जाता है:',
      },
      answer: {
        english: '12 weeks of pregnancy',
        hindi: 'गर्भावस्था के 12 सप्ताह',
      },
      solution: {
        english: 'First trimester; risky in second.',
        hindi: 'पहली तिमाही; दूसरे में जोखिम भरा.',
      },
    },
  ],
  next: {
    slug: 'photosynthesis-higher-plants',
    title: 'Photosynthesis in Higher Plants',
  },
};

export default chapter;
